import React, { useState, useRef, useEffect, useContext, useCallback } from 'react';
import { Bot, X, Send, Loader2, Sparkles, AlertTriangle, User, Phone, CreditCard, Maximize2, Minimize2, TrendingUp, Landmark, Activity } from 'lucide-react';
import { API_URL } from "../config/env";
import { AuthContext } from '../context/AuthContext';

interface Message {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  cards?: any[];
  toolsExecuting?: string[];
  suggestedQueries?: string[];
  pendingAction?: {
    id?: string;
    action: string;
    payload: any;
  };
}

const parseInlineFormatting = (text: string) => {
  const parts = text.split(/(\*\*.*?\*\*)/g);
  return parts.map((part, i) => {
    if (part.startsWith('**') && part.endsWith('**')) {
      return (
        <strong key={i} className="font-bold text-slate-900 font-sans">
          {part.slice(2, -2)}
        </strong>
      );
    }
    return part;
  });
};

const renderTableHelper = (rows: string[][], key: number) => {
  const headers = rows[0];
  const dataRows = rows.slice(1);

  return (
    <div key={key} className="my-2.5 overflow-x-auto rounded-lg border border-slate-100 shadow-sm max-w-full">
      <table className="w-full text-left border-collapse font-sans text-xs">
        <thead>
          <tr className="bg-slate-50 border-b border-slate-100">
            {headers.map((h, i) => (
              <th key={i} className="p-2 text-xs font-semibold uppercase tracking-wider text-slate-500 whitespace-nowrap">
                {parseInlineFormatting(h)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {dataRows.map((row, rIdx) => (
            <tr key={rIdx} className="border-b border-slate-100/50 hover:bg-slate-50/50 last:border-0 transition-colors">
              {row.map((cell, cIdx) => (
                <td key={cIdx} className="p-2 text-slate-600">
                  {parseInlineFormatting(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const renderMarkdown = (text: string) => {
  if (!text) return null;

  const rawLines = text.split('\n');
  const elements: React.ReactNode[] = [];
  
  let inTable = false;
  let tableRows: string[][] = [];

  for (let i = 0; i < rawLines.length; i++) {
    const line = rawLines[i].trim();
    
    // Table row matching
    if (line.startsWith('|') && line.endsWith('|')) {
      if (line.includes('---')) continue;
      
      const cells = line.split('|')
        .map(c => c.trim())
        .filter((_, idx, arr) => idx > 0 && idx < arr.length - 1);
      
      tableRows.push(cells);
      inTable = true;
      continue;
    }
    
    // Render accumulated table if table ended
    if (inTable && tableRows.length > 0) {
      elements.push(renderTableHelper(tableRows, elements.length));
      tableRows = [];
      inTable = false;
    }

    if (!line) {
      elements.push(<div key={`empty-${i}`} className="h-1.5" />);
      continue;
    }

    // Headings
    if (line.startsWith('###')) {
      elements.push(
        <h4 key={i} className="text-[11px] font-bold text-slate-800 mt-3 mb-1 font-sans">
          {parseInlineFormatting(line.replace(/^###\s*/, ''))}
        </h4>
      );
    } else if (line.startsWith('##')) {
      elements.push(
        <h3 key={i} className="text-xs font-bold text-slate-900 mt-4 mb-1.5 font-sans">
          {parseInlineFormatting(line.replace(/^##\s*/, ''))}
        </h3>
      );
    } else if (line.startsWith('#')) {
      elements.push(
        <h2 key={i} className="text-sm font-bold text-slate-950 mt-4 mb-2 font-sans">
          {parseInlineFormatting(line.replace(/^#\s*/, ''))}
        </h2>
      );
    }
    // Lists
    else if (line.startsWith('-') || line.startsWith('*')) {
      const content = line.replace(/^[-*]\s*/, '');
      elements.push(
        <div key={i} className="flex items-start gap-2 ml-1.5 my-1">
          <span className="w-1 h-1 rounded-full bg-blue-500 shrink-0 mt-[6px]" />
          <span className="text-xs text-slate-600 font-sans leading-relaxed">
            {parseInlineFormatting(content)}
          </span>
        </div>
      );
    }
    // Normal paragraph
    else {
      elements.push(
        <p key={i} className="text-xs text-slate-600 font-sans leading-relaxed my-0.5">
          {parseInlineFormatting(line)}
        </p>
      );
    }
  }

  // Trailing table catch
  if (inTable && tableRows.length > 0) {
    elements.push(renderTableHelper(tableRows, elements.length));
  }

  return elements;
};

// 📚 COMPREHENSIVE PUBLIC WEBSITE PRODUCT FAQs
const PUBLIC_FAQS = [
  {
    keywords: ['pricing', 'cost', 'charge', 'plan', 'subscription', 'price', 'gold', 'diamond', 'platinum', 'fee', 'monthly', 'rs', 'rupee', 'money', 'payment'],
    answer: "### PropertyDirect Pricing Plans\n\nWe offer simple, transparent subscription plans based on the size of your society:\n\n* **Gold Plan**: **₹399/month** — Best for small apartments (up to 20 flats). Includes core payment collection, automated digital receipts, and basic WhatsApp alerts.\n* **Diamond Plan**: **₹699/month** — Most popular for medium societies (20-100 flats). Includes the full automated billing suite, mobile OCR bill scanning, and advanced ledger reporting.\n* **Platinum Plan**: **₹999/month** — For enterprise societies (100+ flats). Includes custom developer API access, dedicated premium support, and unlimited admin log-ins.\n\n*All plans feature **0% platform commission** on dues collected!*",
    suggestions: ["Is there a setup fee?", "How does payment collection work?", "How long does onboarding take?"]
  },
  {
    keywords: ['features', 'what does it do', 'capabilities', 'core', 'benefit', 'why choose', 'automation', 'module', 'system'],
    answer: "### PropertyDirect Core Features\n\nPropertyDirect is the complete operations platform for apartment society management — built for real operational scale:\n\n1. **Automated Dues Collection**: Auto-generate monthly invoices for every flat, deliver them instantly via WhatsApp, and accept 1-click payments via UPI, GPay, PhonePe, cards, or netbanking — powered by NoCash Payments Infrastructure.\n2. **Resident Self-Service Portal**: Residents get a personalized portal to view monthly statements, download PDF receipts, and track their full payment history — no app download needed.\n3. **Smart WhatsApp & Email Alerts**: Auto-send bills, payment confirmations, and reminders via WhatsApp or email. Admins control exactly which events fire on which channel using the built-in **Notification Preferences** matrix.\n4. **Smart Utility Billing**: Automate complex water consumption splits, electricity submetering, and late fee penalty calculations — closed-cycle locking prevents any edits after finalization.\n5. **Field Staff Scanner App**: Empower your security or maintenance staff with a dedicated mobile scanner app to capture meter readings and vendor bills via phone camera — instantly synced to the admin portal.\n6. **Instant Revised Invoices**: When a bill is updated, residents automatically receive a revised invoice notification with a clear **[REVISED]** label — no manual re-sending needed.",
    suggestions: ["Tell me about the resident portal", "How does payment collection work?", "Show pricing plans"]
  },
  {
    keywords: ['onboarding', 'set up', 'migration', 'start', 'how to join', 'time', 'transition', 'onboard', 'data', 'import', 'excel', 'setup'],
    answer: "### 24-Hour Express Onboarding\n\nGetting started with PropertyDirect is incredibly fast and completely hands-free:\n\n1. **Zero Upfront Cost**: We charge absolutely zero fees for setup or data migration.\n2. **Hassle-Free Migration**: Simply hand over your flat lists, resident details, and historical balance sheets in any format (Excel files, PDFs, or photos).\n3. **Authoritative Setup**: Our onboarding team standardizes, sanitizes, and securely imports your records into the system.\n4. **Seamless Go-Live**: Your portal is active within **24 hours**, and automatic WhatsApp invitations are sent to invite residents to pay.",
    suggestions: ["Is my society's money safe?", "Do residents need an app?", "How to book a demo?"]
  },
  {
    keywords: ['safe', 'security', 'money', 'bank', 'direct', 'wallet', 'commission', 'account', 'gateway', 'trust', 'secure', 'transfer', 'nocash'],
    answer: "### Absolute Financial Security\n\nYour society's funds are 100% secure with PropertyDirect:\n\n* **Direct Bank Routing via NoCash**: We are **not a wallet**. 100% of resident maintenance payments are processed through **NoCash Payments Infrastructure** and flow directly into your society's official, registered bank account — verified during onboarding with IFSC + account validation.\n* **0% Platform Commission**: We do not take a cut of your dues. You only pay standard gateway processing fees.\n* **Real-time Reconciliation**: Admins have real-time visibility into every transaction, matching invoices to bank deposits automatically with zero manual entry.\n* **UPI Static QR**: Your society gets a permanent UPI QR code linked directly to your bank account for walk-in payment collection.",
    suggestions: ["Show pricing plans", "How does onboarding work?", "How does payment collection work?"]
  },
  {
    keywords: ['whatsapp', 'alert', 'message', 'reminder', 'receipt', 'notice', 'sms', 'notification', 'phone', 'email', 'channel'],
    answer: "### Smart Multi-Channel Communications\n\nNo more paper bills or ignored emails! PropertyDirect reaches residents on the right channel at the right time:\n\n* **Instant Bill Delivery**: The moment invoices are generated, WhatsApp messages and emails fire immediately — not at a scheduled time.\n* **Revised Invoice Alerts**: If a bill is updated, residents automatically receive a **[REVISED]** invoice notification so they always have the latest amount.\n* **Friendly Reminders**: Personalized, automated payment reminders are sent to pending accounts at Day 0, 3, and 7 — resulting in **3x faster collection rates**.\n* **Instant PDF Receipts**: Once payment succeeds, a PDF receipt is delivered to the resident's WhatsApp and email simultaneously.\n* **Admin Notification Control**: Admins use the **Notification Preferences** panel to control which events (bills, reminders, receipts, notices) fire via WhatsApp vs Email — or both.",
    suggestions: ["Do residents need to download an app?", "How does payment collection work?", "Tell me about the resident portal"]
  },
  {
    keywords: ['app', 'download', 'install', 'mobile', 'play store', 'app store', 'resident', 'user', 'downloading', 'staff', 'scanner'],
    answer: "### Apps by Role — Only Where It Makes Sense\n\n**Residents & Flat Owners — Zero Install Required:**\n\n* **WhatsApp-Driven**: Invoices, reminders, and PDF receipts arrive directly in WhatsApp chats.\n* **Resident Self-Service Portal**: A secure link in the WhatsApp message opens a personalized portal — view statements, download PDFs, and track full payment history with no login or app needed.\n* **1-Click Payments**: Complete payments via GPay, PhonePe, UPI, Netbanking, or cards in seconds.\n\n**Field Staff (Security / Maintenance) — Dedicated Scanner App:**\n\n* **Mobile Scanner App**: Staff granted scanner access use a dedicated mobile web app to capture meter readings (water, electricity) and vendor bills via phone camera — readings are instantly synced to the admin portal.\n\n**Society Admins & Managers — Web Dashboard:**\n\n* Full operations dashboard accessible from any browser — billing, ledger, payments, reports, and notification management.",
    suggestions: ["Tell me about the resident portal", "How does payment collection work?", "What are the core features?"]
  },
  {
    keywords: ['ocr', 'scan', 'camera', 'meter', 'reading', 'electricity', 'water', 'pump', 'vendor', 'invoice', 'photo', 'scanning', 'field', 'staff'],
    answer: "### Field Staff Scanner App — OCR Bill & Meter Scanning\n\nPropertyDirect includes a dedicated mobile scanner app for field staff (security, maintenance):\n\n* **Vendor Bill Digitization**: Snap a photo of any physical invoice (diesel, repairs, security service). The AI instantly extracts the supplier name, invoice date, and total amount — creating a staged expense ledger entry for admin approval.\n* **Water Meter Scanning**: Capture a photo of individual flat submeters. The scanner reads the digit display, matches it to the flat, and computes consumption in real time.\n* **Role-Gated Access**: Scanner app access is controlled at the admin level per staff role — only authorized field staff can use it. Regular residents and accountants are unaffected.\n* **Instant Sync**: All captured readings and bills sync immediately to the admin web portal — no manual data entry, no transcription errors.",
    suggestions: ["Show pricing plans", "What are the core features?", "How do apps work for residents?"]
  },
  {
    keywords: ['demo', 'contact', 'call', 'support', 'sales', 'reach', 'number', 'phone', 'email', 'talk', 'person', 'representative', 'inquiry'],
    answer: "### Get in Touch With Us!\n\nWe would love to show you a live demonstration of PropertyDirect:\n\n* **Direct Sales / WhatsApp**: **+91 91105 13749**\n* **Email Inquiries**: **support@propertydirect.in**\n* **Book a Demo**: Fill out the form at the bottom of our landing page, and our onboarding success team will get back to you within **2 hours**!",
    suggestions: ["How does onboarding work?", "Show pricing plans"]
  },
  {
    keywords: ['login', 'log in', 'signin', 'sign in', 'access', 'auth', 'credentials', 'portal', 'dashboard'],
    answer: "### How to Log In & Access Your Portal\n\nAccessing PropertyDirect is tailored by role — everyone has the right entry point:\n\n* **Society Managers & Admins**: Click **Login** in the top navigation bar or go to `/auth`. Enter your credentials to access the full operations dashboard — billing, payments, ledger, reports, and staff management.\n* **Residents & Flat Owners**: No password needed. Click the secure link sent to your WhatsApp to open your **Resident Portal** — view statements, download PDF receipts, and track payment history instantly.\n* **Field Staff**: Staff granted scanner app access log in with their staff credentials to access the dedicated mobile scanner for meter readings and vendor bills.\n\nIf your account is not yet configured, contact our onboarding team at **+91 91105 13749**.",
    suggestions: ["Tell me about the resident portal", "Do residents need an app?", "What are the core features?"]
  },
  {
    keywords: ['payment collection', 'how to pay', 'payment gateway', 'upi', 'credit card', 'netbanking', 'dues', 'invoice', 'gateway', 'pay maintenance', 'qr', 'qr code'],
    answer: "### Next-Gen Payment Collection — Powered by NoCash\n\nPropertyDirect automates the full maintenance collection cycle end-to-end:\n\n1. **Auto-Billing & Invoices**: The platform generates digital statements for every flat. On first generation, WhatsApp messages and emails are dispatched **immediately** — not on a delayed schedule.\n2. **WhatsApp & Email Delivery**: Each resident receives a personalized message with a secure, unique checkout link via WhatsApp and/or email (admin-configurable per event type).\n3. **1-Click Checkout via NoCash**: The link opens a mobile-optimized checkout page powered by **NoCash Payments Infrastructure** — accepts GPay, PhonePe, UPI, credit/debit cards, and netbanking in seconds.\n4. **Static UPI QR Code**: Your society also receives a permanent UPI QR code linked directly to your bank account for walk-in or counter payment collection.\n5. **Instant Receipts & Auto-Reconciliation**: Payment confirmed → funds go directly to your society's bank → resident gets an instant PDF receipt on WhatsApp → invoice is marked reconciled. Zero manual effort.",
    suggestions: ["Is my society's money safe?", "Tell me about the resident portal", "How does onboarding work?"]
  },
  {
    keywords: ['resident portal', 'resident', 'statement', 'my payments', 'my bills', 'payment history', 'download statement', 'view invoice', 'outstanding', 'ledger', 'balance'],
    answer: "### Resident Self-Service Portal\n\nPropertyDirect gives every resident their own secure self-service portal — no app download, no registration:\n\n* **Monthly Statements**: Residents can view a full charge breakdown for any month — Maintenance, Water, Electricity, Common Area charges, and Penalties — all in one screen.\n* **Payment History**: See all payments in real time with full status tracking — Paid, Processing, Failed, and Initiated states are all visible.\n* **Download PDF Statements**: Generate and download a professionally formatted A4 statement PDF at any time, pre-populated with flat details, resident name, and the billing period.\n* **Receipt Downloads**: Download PDF receipts for any individual payment directly from the portal.\n* **Zero Login Friction**: Residents access the portal via a secure, unique link delivered to their WhatsApp — no username, no password.",
    suggestions: ["Do residents need to download an app?", "How does payment collection work?", "Tell me about WhatsApp alerts"]
  },
  {
    keywords: ['free trial', 'trial', 'setup fee', 'setup cost', 'test', 'demo', 'try it out', 'guarantee', 'cancel'],
    answer: "### 100% Risk-Free 14-Day Trial\n\nWe believe in our product, which is why we offer a completely risk-free trial:\n\n* **14-Day Free Trial**: Get full access to the Diamond Plan features for 14 days, with zero upfront commitment.\n* **Zero Setup Fees**: We do not charge anything for setup, integration, or custom Excel migration.\n* **Free Data Import**: Hand over your flat lists, resident records, and active arrears in any format (Excel, photos, PDFs) and our dedicated success team will migrate it for free.\n* **No Credit Card Required**: You can test the entire platform without entering any billing details.",
    suggestions: ["How does onboarding work?", "What are the pricing plans?", "What are the core features?"]
  }
];

const getPublicFAQResponse = (text: string) => {
  const query = text.toLowerCase();
  
  let bestMatch: typeof PUBLIC_FAQS[0] | null = null;
  let highestScore = 0;
  
  for (const faq of PUBLIC_FAQS) {
    let score = 0;
    for (const kw of faq.keywords) {
      if (query.includes(kw)) {
        score += 1;
        // Boost score if keyword matches a full word
        const regex = new RegExp(`\\b${kw}\\b`, 'i');
        if (regex.test(query)) {
          score += 2;
        }
      }
    }
    if (score > highestScore) {
      highestScore = score;
      bestMatch = faq;
    }
  }
  
  if (highestScore > 0 && bestMatch) {
    return {
      content: bestMatch.answer,
      suggestedQueries: bestMatch.suggestions
    };
  }
  
  // Default fallback if no keywords match
  return {
    content: "I am Orbit, the PropertyDirect public assistant. I can answer questions about our product features, pricing, setup, and support.\n\nCould you please ask me something about:\n\n* **Features** (e.g. 'What are the core features?')\n* **Pricing** (e.g. 'How much does it cost?')\n* **Resident Portal** (e.g. 'How does the resident portal work?')\n* **Payment Collection** (e.g. 'How does payment collection work?')\n* **Security** (e.g. 'Is my society money safe?')\n* **WhatsApp & Notifications** or **OCR Bill Scanning**.\n\n*For tenant operations, defaulter lists, and ledger queries — please log into your dashboard.*",
    suggestedQueries: [
      "What are the core features?",
      "How much does it cost?",
      "Tell me about the resident portal",
      "How does payment collection work?",
      "Is my society's money safe?",
      "How does onboarding work?"
    ]
  };
};

// ─── Page-aware greeting ──────────────────────────────────────────────────────

const PAGE_GREETINGS: Record<string, { label: string; suggestions: string[] }> = {
  "water-billing":       { label: "Water Billing",            suggestions: ["Show water billing status this month", "List water bills", "Show water purchases"] },
  "electricity":         { label: "Electricity Billing",      suggestions: ["Show electricity bills this month", "List electricity bills"] },
  "payments":            { label: "Payments & Collections",   suggestions: ["List recent payments", "Show collection rate this month", "Who has unpaid dues?"] },
  "residents":           { label: "Residents",                suggestions: ["Search for a resident", "Show occupancy stats", "List all residents"] },
  "owners":              { label: "Flat Owners",              suggestions: ["Search for an owner", "List all owners"] },
  "treasury":            { label: "Treasury",                 suggestions: ["Show treasury balances", "Show active system alerts", "What should I do today?"] },
  "bank-accounts":       { label: "Bank Accounts",            suggestions: ["Show treasury balances", "Check bank account balance"] },
  "cash-in-hand":        { label: "Cash in Hand",             suggestions: ["Show treasury balances", "What should I do today?"] },
  "reconciliation":      { label: "Bank Reconciliation",      suggestions: ["Show active system alerts", "Show treasury balances", "List recent payments"] },
  "expenses":            { label: "Expenses & Expenditure",   suggestions: ["Show expense ledger", "What should I do today?"] },
  "billing":             { label: "Billing",                  suggestions: ["Show revenue summary this month", "Who has unpaid dues?", "Show defaulters"] },
  "demands":             { label: "Maintenance Demands",      suggestions: ["Show maintenance bills this month", "Show collection rate", "Show defaulters"] },
  "defaulters":          { label: "Defaulters",               suggestions: ["Show all defaulters", "Show revenue summary", "What should I do today?"] },
  "reports":             { label: "Reports",                  suggestions: ["Show revenue summary this month", "Show billing summary", "Show water bills"] },
  "financial-health":    { label: "Financial Health",         suggestions: ["Show revenue summary this month", "Show defaulters", "What should I do today?"] },
  "smart-import-studio": { label: "Smart Import Studio",      suggestions: ["What should I do today?", "Show active system alerts"] },
  "notices":             { label: "Notices & Announcements",  suggestions: ["Show occupancy stats", "List all residents", "What should I do today?"] },
  "staff":               { label: "Staff Management",         suggestions: ["List all staff", "Search for a staff member"] },
  "suppliers":           { label: "Suppliers & Vendors",      suggestions: ["List all suppliers", "Search for a supplier"] },
  "funds":               { label: "Funds",                    suggestions: ["Show treasury balances", "What should I do today?"] },
};

function buildPageGreeting(pathname: string): { content: string; suggestedQueries: string[] } {
  const segments = pathname.replace(/^\/dashboard\/?/, "").split("/");
  const ctx = PAGE_GREETINGS[segments[0]];
  if (ctx) {
    return {
      content: `You're on **${ctx.label}**. What do you need?`,
      suggestedQueries: ctx.suggestions
    };
  }
  return {
    content: "I'm PD Orbit, your society operations assistant. I can look up residents, payments, defaulters, billing, treasury balances, and more. What do you need?",
    suggestedQueries: ["What should I do today?", "Who has unpaid dues this month?", "Show revenue summary", "Show treasury balances"]
  };
}

const TOOL_LABELS: Record<string, string> = {
  getResidentDetails:      "Looking up resident...",
  searchResident:          "Searching residents...",
  listResidents:           "Loading residents...",
  listOwners:              "Loading flat owners...",
  searchOwner:             "Searching owners...",
  listStaff:               "Loading staff directory...",
  searchStaff:             "Searching staff...",
  listSuppliers:           "Loading suppliers...",
  searchSupplier:          "Searching suppliers...",
  getPaymentStatus:        "Checking payment status...",
  getPendingBills:         "Finding pending bills...",
  getUnitSummary:          "Loading unit summary...",
  getDefaulters:           "Finding defaulters...",
  getLastPayments:         "Loading payment history...",
  getFlatOccupancyStats:   "Checking occupancy...",
  getRevenueSummary:       "Analysing revenue...",
  getFlatsList:            "Loading units...",
  getPaymentsList:         "Loading payments...",
  getElectricityBillsList: "Loading electricity bills...",
  getWaterBillsList:       "Loading water bills...",
  getWaterPurchasesList:   "Loading water purchases...",
  getExpensesList:         "Loading expenses...",
  getBillingSummaryList:   "Loading billing summary...",
  getMaintenanceBillsList: "Loading maintenance bills...",
  getTreasuryBalances:     "Checking treasury balances...",
  getActiveAnomalies:      "Checking system health...",
  getDailyBriefing:        "Preparing daily briefing...",
  sendReminderNotice:      "Preparing payment reminder...",
  getPlatformNavigation:   "Finding the right screen...",
};

const isMac = typeof navigator !== 'undefined' && navigator.platform.toUpperCase().includes('MAC');
const ORBIT_SHORTCUT = isMac ? '⌘K' : 'Ctrl + K';
// Must be >= the chat panel's Tailwind duration-300 animation
const PANEL_OPEN_DELAY = 350;

export default function AICopilot() {
  const { user, platformUser } = useContext(AuthContext);
  const [isOpen, setIsOpen] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [showTeaser, setShowTeaser] = useState(false);

  const isPlatformRoute = window.location.pathname.includes("/platform");
  const isDashboardRoute = window.location.pathname.includes("/dashboard");
  const isInPortal = isPlatformRoute || isDashboardRoute;
  const activeUser = isPlatformRoute ? platformUser : user;
  const token = activeUser?.token || "";
  const isPublic = !token || !isInPortal;

  const [messages, setMessages] = useState<Message[]>([
    {
      id: 'init',
      role: 'assistant',
      content: "Hello! I'm PD Orbit, your PropertyDirect assistant. How can I help you discover PropertyDirect today? I can answer questions about our features, pricing, and onboarding process!",
      suggestedQueries: [
        "What are the core features?",
        "How much does it cost?",
        "Tell me about the resident portal",
        "How does payment collection work?",
        "Is my society's money safe?",
        "How does onboarding work?"
      ]
    }
  ]);

  useEffect(() => {
    if (!isOpen) {
      const timer = setTimeout(() => {
        setShowTeaser(true);
      }, 1500);
      return () => clearTimeout(timer);
    } else {
      setShowTeaser(false);
    }
  }, [isOpen]);

  const pendingPromptRef = useRef<string | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      const prompt = (e as CustomEvent).detail?.prompt as string | undefined;
      if (!isPublic && messages.length <= 1) {
        const greeting = buildPageGreeting(window.location.pathname);
        setMessages([{ id: 'init', role: 'assistant', content: greeting.content, suggestedQueries: greeting.suggestedQueries }]);
      }
      if (prompt && !isPublic) pendingPromptRef.current = prompt;
      setIsOpen(true);
    };
    window.addEventListener('pd-orbit:open', handler);
    return () => window.removeEventListener('pd-orbit:open', handler);
  }, [isPublic, messages.length]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        if (isPublic) return;
        if (!isOpen && messages.length <= 1) {
          const greeting = buildPageGreeting(window.location.pathname);
          setMessages([{ id: 'init', role: 'assistant', content: greeting.content, suggestedQueries: greeting.suggestedQueries }]);
        }
        setIsOpen(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, isPublic, messages.length]);

  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Stable ref so handleSend can read latest messages without capturing them in deps
  const messagesRef = useRef(messages);
  useEffect(() => { messagesRef.current = messages; }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isTyping]);

  useEffect(() => {
    if (isPublic) {
      setMessages([
        {
          id: 'init',
          role: 'assistant',
          content: "Hello! I'm PD Orbit, your PropertyDirect assistant. How can I help you discover PropertyDirect today? I can answer any questions you have about our features, pricing, and onboarding process!",
          suggestedQueries: [
            "What are the core features?",
            "How much does it cost?",
            "Tell me about the resident portal",
            "How does payment collection work?",
            "Is my society's money safe?",
            "How does onboarding work?"
          ]
        }
      ]);
    } else {
      const greeting = buildPageGreeting(window.location.pathname);
      setMessages([{ id: 'init', role: 'assistant', content: greeting.content, suggestedQueries: greeting.suggestedQueries }]);
    }
  }, [isPublic]);

  const handleSend = useCallback(async (text: string = input) => {
    if (!text.trim()) return;

    const userMsgId = Date.now().toString();
    const userMsg = { id: userMsgId, role: 'user' as const, content: text };
    // Build newMessages from ref (stable, no closure over state)
    const newMessages = [...messagesRef.current, userMsg];
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsTyping(true);

    const assistantMsgId = (Date.now() + 1).toString();

    if (isPublic) {
      // 🛡️ PUBLIC OUTSIDE LOGIN INTERCEPTOR: No LLM Hits!
      setTimeout(() => {
        const match = getPublicFAQResponse(text);
        setMessages(prev => [
          ...prev,
          {
            id: assistantMsgId,
            role: 'assistant',
            content: match.content,
            suggestedQueries: match.suggestedQueries
          }
        ]);
        setIsTyping(false);
      }, 500); // 500ms transition to emulate response processing
      return;
    }

    // 🧠 INSIDE LOGIN: Hit the streaming LLM API
    setMessages(prev => [...prev, { id: assistantMsgId, role: 'assistant', content: '', cards: [], toolsExecuting: [] }]);

    try {
      const history = newMessages.slice(-5).map(m => ({ role: m.role, content: m.content }));
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const fetchUrl = isLocal ? `${API_URL}/api/ai/stream` : `/api/ai/stream`;
      
      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ message: text, history: history.slice(0, -1), pageContext: window.location.pathname })
      });

      if (!response.body) throw new Error('No readable stream');
      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      let assistantContent = '';
      let currentCards: any[] = [];
      let currentTools: string[] = [];
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        
        if (value) {
          buffer += decoder.decode(value, { stream: true });
        }

        const parts = buffer.split('\n\n');
        if (done) {
          buffer = '';
        } else {
          buffer = parts.pop() || '';
        }

        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed) continue;

          if (trimmed.startsWith('data: ')) {
            try {
              const event = JSON.parse(trimmed.substring(6));
              
              if (event.type === 'content') {
                assistantContent += event.text;
              } else if (event.type === 'tool_start') {
                currentTools = [...currentTools, event.tool];
              } else if (event.type === 'tool_end') {
                currentTools = currentTools.filter(t => t !== event.tool);
              } else if (event.type === 'ui_card') {
                currentCards = [...currentCards, { type: event.cardType, data: event.data }];
              } else if (event.type === 'suggested_queries') {
                setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, suggestedQueries: event.queries } : m));
              } else if (event.type === 'pending_action') {
                setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, pendingAction: { id: event.pendingActionId, action: event.action, payload: event.payload } } : m));
              } else if (event.type === 'error') {
                assistantContent = event.message
                  ? `Sorry, I ran into an issue: ${event.message}`
                  : "Sorry, I encountered an error while processing that.";
              }

              setMessages(prev => prev.map(m => 
                m.id === assistantMsgId ? { 
                  ...m, 
                  content: assistantContent, 
                  cards: currentCards,
                  toolsExecuting: currentTools 
                } : m
              ));

            } catch (e) {
              console.error("Error parsing SSE JSON:", e);
            }
          }
        }

        if (done) break;
      }

    } catch (error) {
      console.error('AI Error:', error);
      setMessages(prev => prev.map(m => m.id === assistantMsgId ? { ...m, content: "Sorry, I encountered an error connecting to the AI brain." } : m));
    } finally {
      setIsTyping(false);
    }
  }, [input, isPublic, token]);

  // Fire any prompt that arrived via pd-orbit:open detail.prompt.
  // 350ms delay matches the panel's duration-300 animation plus a small buffer.
  useEffect(() => {
    if (!isOpen || !pendingPromptRef.current || isTyping) return;
    const prompt = pendingPromptRef.current;
    pendingPromptRef.current = null;
    const timer = setTimeout(() => handleSend(prompt), PANEL_OPEN_DELAY);
    return () => clearTimeout(timer);
  }, [isOpen, isTyping, handleSend]);

  const [isExecutingAction, setIsExecutingAction] = useState(false);

  const handleApproveAction = async (msgId: string, actionId: string) => {
    if (!actionId) {
      alert("No action ID found. Action cannot be approved.");
      return;
    }
    setIsExecutingAction(true);
    
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, toolsExecuting: ["Approve Action"] } : m));

    try {
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      const fetchUrl = isLocal ? `${API_URL}/api/ai/approve-action` : `/api/ai/approve-action`;

      const response = await fetch(fetchUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ actionId })
      });

      const resData = await response.json();

      if (response.ok && resData.success) {
        setMessages(prev => prev.map(m => 
          m.id === msgId 
            ? { 
                ...m, 
                content: m.content + `\n\n**✅ Action Approved and Executed Successfully:**\n${resData.message || ''}`,
                pendingAction: undefined, 
                toolsExecuting: [] 
              } 
            : m
        ));
      } else {
        alert(resData.error || "Failed to execute approved action.");
        setMessages(prev => prev.map(m => m.id === msgId ? { ...m, toolsExecuting: [] } : m));
      }
    } catch (err: any) {
      console.error("Failed to approve action:", err);
      alert("Failed to connect to approval gateway.");
      setMessages(prev => prev.map(m => m.id === msgId ? { ...m, toolsExecuting: [] } : m));
    } finally {
      setIsExecutingAction(false);
    }
  };

  const handleCancelAction = (msgId: string) => {
    setMessages(prev => prev.map(m => m.id === msgId ? { ...m, pendingAction: undefined } : m));
  };

  const renderCard = (card: any, index: number) => {
    switch (card.type) {
      case 'resident_card':
        return (
          <div key={index} className="bg-slate-50/50 border border-slate-100 rounded-xl p-3.5 my-2 shadow-sm flex items-start gap-3 hover:bg-slate-50 transition-colors">
            <div className="w-9 h-9 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-slate-400 mb-0.5">Resident Profile</div>
              <div className="font-bold text-sm text-slate-800 truncate">{card.data.name || card.data.owner?.name || "Unknown"}</div>
              <div className="text-xs text-slate-500 font-medium">Flat {card.data.flatNo}</div>
              {card.data.phone && (
                <div className="text-[11px] text-slate-400 mt-1.5 flex items-center gap-1">
                  <Phone className="w-3 h-3" /> {card.data.phone}
                </div>
              )}
            </div>
          </div>
        );
      case 'payment_card':
        return (
          <div key={index} className="bg-slate-50/50 border border-slate-100 rounded-xl p-3.5 my-2 shadow-sm flex items-start gap-3 hover:bg-slate-50 transition-colors">
            <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${card.data.result ? 'bg-emerald-50' : 'bg-rose-50'}`}>
              <CreditCard className={`w-4 h-4 ${card.data.result ? 'text-emerald-600' : 'text-rose-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-slate-400 mb-0.5">Payment Verification</div>
              {card.data.result ? (
                <>
                  <div className="font-bold text-sm text-emerald-600 flex items-center gap-1.5">
                    Verified Paid
                  </div>
                  <div className="text-[11px] text-slate-500 font-medium mt-0.5">Bill Type: {card.data.billType}</div>
                </>
              ) : (
                <div className="font-bold text-sm text-rose-500">Unpaid or No Record</div>
              )}
            </div>
          </div>
        );
      case 'defaulter_card':
        return (
          <div key={index} className="bg-rose-50/20 border border-rose-100 rounded-xl p-3.5 my-2 shadow-sm flex items-start gap-3">
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-rose-400 mb-0.5">Defaulter Alert</div>
              {Array.isArray(card.data) ? (
                <div className="text-xs text-rose-800 font-medium">
                  Found <span className="font-bold text-rose-900 text-sm">{card.data.length}</span> defaulters pending payment.
                </div>
              ) : (
                <div className="text-xs text-rose-800 font-medium">Outstanding pending dues found.</div>
              )}
            </div>
          </div>
        );
      case 'unit_summary_card':
        return (
          <div key={index} className="bg-slate-50/50 border border-slate-100 rounded-xl p-3.5 my-2 shadow-sm flex items-start gap-3 hover:bg-slate-50 transition-colors">
            <div className="w-9 h-9 rounded-lg bg-indigo-50 flex items-center justify-center shrink-0">
              <CreditCard className="w-4 h-4 text-indigo-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold text-slate-400 mb-0.5">Unit Summary • {card.data.flatNo}</div>
              <div className="text-xs text-slate-600 mt-1">
                Current Outstanding Balance: <span className="font-bold text-sm text-slate-800">₹{card.data.currentBalance || 0}</span>
              </div>
            </div>
          </div>
        );
      case 'daily_briefing': {
        const b = card.data;
        const rate = b.billing?.collectionRate ?? 0;
        const items: { dot: string; text: React.ReactNode }[] = [];
        if (b.billing?.pendingDuesCount > 0)
          items.push({ dot: rate < 80 ? 'bg-amber-500' : 'bg-blue-500', text: <><strong>{b.billing.pendingDuesCount}</strong> maintenance dues pending in {b.month}</> });
        if (b.billing?.collectionRate !== undefined)
          items.push({ dot: rate >= 80 ? 'bg-emerald-500' : 'bg-amber-500', text: <>Revenue collection at <strong>{rate}%</strong> this month</> });
        if (b.anomalies?.activeCount > 0)
          items.push({ dot: 'bg-rose-500', text: <><strong>{b.anomalies.activeCount}</strong> active system alert{b.anomalies.activeCount !== 1 ? 's' : ''} detected</> });
        if (b.reconciliation?.unmatchedBankRows > 0)
          items.push({ dot: 'bg-amber-500', text: <><strong>{b.reconciliation.unmatchedBankRows}</strong> unmatched bank statement {b.reconciliation.unmatchedBankRows === 1 ? 'row' : 'rows'} pending reconciliation</> });
        if (b.imports?.failedRows > 0)
          items.push({ dot: 'bg-amber-500', text: <><strong>{b.imports.failedRows}</strong> imported {b.imports.failedRows === 1 ? 'row' : 'rows'} failed and need review</> });
        return (
          <div key={index} className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 my-2 space-y-2.5">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="w-3.5 h-3.5 text-blue-500 shrink-0" />
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Today's Highlights · {b.month} {b.year}</span>
            </div>
            {items.length === 0 ? (
              <p className="text-xs text-emerald-700 font-medium">Everything looks good — no pending actions today.</p>
            ) : (
              items.map((item, i) => (
                <div key={i} className="flex items-start gap-2">
                  <span className={`w-2 h-2 rounded-full shrink-0 mt-[4px] ${item.dot}`} />
                  <span className="text-xs text-slate-700 leading-relaxed">{item.text}</span>
                </div>
              ))
            )}
          </div>
        );
      }

      case 'treasury_card': {
        const t = card.data;
        if (t.error) return (
          <div key={index} className="bg-amber-50 border border-amber-200 rounded-xl p-3 my-2 text-xs text-amber-800">{t.error}</div>
        );
        return (
          <div key={index} className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 my-2 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Landmark className="w-3.5 h-3.5 text-blue-600 shrink-0" />
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">Treasury · {t.accountCount} Account{t.accountCount !== 1 ? 's' : ''}</span>
            </div>
            {(t.accounts || []).map((a: any, i: number) => (
              <div key={i} className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-semibold text-slate-800 truncate">{a.name}</div>
                  <div className="text-[10px] text-slate-400">{a.type}{a.bank ? ` · ${a.bank}` : ''}{a.isPrimary ? ' · Primary' : ''}</div>
                </div>
                <div className="text-sm font-bold text-slate-800 shrink-0">₹{a.balance.toLocaleString('en-IN')}</div>
              </div>
            ))}
            {t.accountCount > 1 && (
              <div className="pt-1.5 border-t border-slate-200 flex justify-between text-xs font-bold text-slate-700">
                <span>Total</span>
                <span>₹{(t.totalBalance || 0).toLocaleString('en-IN')}</span>
              </div>
            )}
          </div>
        );
      }

      case 'anomaly_card': {
        const a = card.data;
        if (a.totalOpen === 0) return (
          <div key={index} className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 my-2 flex items-center gap-2">
            <Activity className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
            <span className="text-xs text-emerald-800 font-medium">No active anomalies — system is operating normally.</span>
          </div>
        );
        const severityDot = (s: string) => s === 'CRITICAL' ? 'bg-rose-500' : s === 'HIGH' ? 'bg-amber-500' : 'bg-blue-400';
        return (
          <div key={index} className="bg-slate-50 border border-slate-200/80 rounded-xl p-3.5 my-2 space-y-2">
            <div className="flex items-center gap-2 mb-1">
              <Activity className="w-3.5 h-3.5 text-rose-500 shrink-0" />
              <span className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                {a.totalOpen} Active Alert{a.totalOpen !== 1 ? 's' : ''}{a.criticalCount > 0 ? ` · ${a.criticalCount} Critical` : ''}
              </span>
            </div>
            {[...(a.incidents || []), ...(a.anomalies || [])].slice(0, 6).map((item: any, i: number) => (
              <div key={i} className="flex items-start gap-2">
                <span className={`w-2 h-2 rounded-full shrink-0 mt-[4px] ${severityDot(item.severity)}`} />
                <div className="min-w-0">
                  <div className="text-xs text-slate-700 leading-relaxed">{item.message}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5">{item.type} · {item.detectedAt}</div>
                </div>
              </div>
            ))}
          </div>
        );
      }

      default:
        return null;
    }
  };

  return (
    <>
      <style>{`
        @keyframes pdBounceSubtle {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-pd-bounce-subtle {
          animation: pdBounceSubtle 3s ease-in-out infinite;
        }
      `}</style>

      {/* Floating Action Button & Teaser Bubble */}
      {!isOpen && (
        <div className="fixed bottom-6 right-6 flex flex-col items-end z-[999] pointer-events-none">
          {/* Teaser Bubble (Glassmorphic & Animated) */}
          {showTeaser && (
            <div className="pointer-events-auto mb-3.5 mr-2 bg-slate-900/95 text-white p-3.5 rounded-2xl shadow-2xl border border-slate-700/60 backdrop-blur-md max-w-[270px] animate-pd-bounce-subtle flex items-start gap-2.5 transition-all duration-500 relative group-hover:scale-102">
              <div className="w-5 h-5 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 shrink-0 mt-0.5 animate-pulse">
                <Sparkles className="w-3.5 h-3.5" />
              </div>
              <div className="flex-1 min-w-0 pr-2">
                <div className="text-[10px] font-bold text-blue-400 mb-0.5">PD Orbit AI Assistant</div>
                <p className="text-[11px] text-slate-200 font-medium leading-relaxed">
                  {isPublic ? (
                    <>
                      Discover PropertyDirect! Ask about our <strong className="font-extrabold text-blue-400 font-sans">features, pricing</strong>, or <strong className="font-extrabold text-blue-400 font-sans">free setup</strong>.
                    </>
                  ) : (
                    <>
                      Need help with operations? Ask about <strong className="font-extrabold text-blue-400 font-sans">defaulters, revenue summary</strong>, or <strong className="font-extrabold text-blue-400 font-sans">billing status</strong>.{' '}
                      <span className="text-slate-400 text-[10px]">Press <kbd className="px-1 py-0.5 bg-slate-700 text-slate-300 rounded text-[9px] font-bold">{ORBIT_SHORTCUT}</kbd> anytime.</span>
                    </>
                  )}
                </p>
              </div>
              <button 
                onClick={(e) => { e.stopPropagation(); setShowTeaser(false); }}
                className="text-slate-400 hover:text-white transition-colors cursor-pointer shrink-0 p-0.5 rounded hover:bg-slate-800"
                title="Dismiss"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          )}
          
          {/* Main Button with Pulse rings */}
          <button
            onClick={() => {
              if (!isPublic && messages.length <= 1) {
                const greeting = buildPageGreeting(window.location.pathname);
                setMessages([{ id: 'init', role: 'assistant', content: greeting.content, suggestedQueries: greeting.suggestedQueries }]);
              }
              setIsOpen(true);
              setShowTeaser(false);
            }}
            className="pointer-events-auto p-4 bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600 text-white rounded-full shadow-2xl hover:scale-110 active:scale-95 transition-all duration-300 flex items-center justify-center gap-2 group border border-blue-400/30 hover:shadow-blue-500/40 cursor-pointer relative"
          >
            {/* Pulsing Outer Rings */}
            <span className="absolute inset-0 rounded-full bg-blue-500/35 animate-ping opacity-60 pointer-events-none" />
            <span className="absolute -inset-1.5 rounded-full border border-indigo-500/25 animate-pulse pointer-events-none" style={{ animationDuration: '2.5s' }} />
            
            <Sparkles className="w-5 h-5 group-hover:rotate-12 transition-transform duration-300 shrink-0" />
            <span className="max-w-0 overflow-hidden whitespace-nowrap group-hover:max-w-xs transition-all duration-300 font-bold text-sm tracking-tight">
              Ask PD Orbit
            </span>
          </button>
        </div>
      )}

      {/* Chat Window */}
      {isOpen && (
        <div 
          className={`fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl border border-slate-200/80 flex flex-col z-[9999] overflow-hidden transition-all duration-300 ${
            isMaximized 
              ? 'w-[85vw] h-[85vh] max-w-[1200px]' 
              : 'w-[400px] h-[640px] max-h-[85vh]'
          }`}
        >
          {/* Header */}
          <div className="bg-slate-950 text-white p-4 flex items-center justify-between border-b border-slate-900 shadow-sm shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-blue-500/10 border border-blue-500/20 flex items-center justify-center">
                <Bot className="w-5 h-5 text-blue-400" />
              </div>
              <div>
                <h3 className="font-bold text-sm tracking-tight font-sans">PD Orbit</h3>
                <p className="text-[9px] text-slate-400 uppercase tracking-widest font-semibold font-sans">AI Operations Engine</p>
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              <button 
                onClick={() => setIsMaximized(!isMaximized)} 
                className="text-slate-400 hover:text-white transition-colors cursor-pointer p-1.5 rounded-lg hover:bg-slate-900"
                title={isMaximized ? "Minimize window" : "Maximize window"}
              >
                {isMaximized ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              <button 
                onClick={() => setIsOpen(false)} 
                className="text-slate-400 hover:text-white transition-colors cursor-pointer p-1.5 rounded-lg hover:bg-slate-900"
                title="Close chat"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-slate-50/50">
            {messages.map((msg) => (
              <div key={msg.id} className={`flex flex-col ${msg.role === 'user' ? 'items-end' : 'items-start'}`}>
                <div 
                  className={`max-w-[88%] p-3.5 rounded-2xl shadow-sm ${
                    msg.role === 'user' 
                      ? 'bg-gradient-to-r from-blue-600 to-indigo-600 rounded-br-none shadow-md shadow-blue-500/10' 
                      : 'bg-white text-slate-800 border border-slate-200/60 rounded-bl-none shadow-sm shadow-slate-100/50'
                  }`}
                >
                  {msg.role === 'assistant' ? (
                    msg.content === '' && isTyping ? (
                      <div className="flex items-center gap-1.5 py-1 px-1.5">
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '0ms' }} />
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '150ms' }} />
                        <span className="w-2 h-2 rounded-full bg-blue-500 animate-bounce" style={{ animationDelay: '300ms' }} />
                      </div>
                    ) : (
                      <div className="space-y-1.5">{renderMarkdown(msg.content)}</div>
                    )
                  ) : (
                    <p className="text-xs whitespace-pre-wrap leading-relaxed font-sans text-white font-medium">{msg.content}</p>
                  )}
                  
                  {/* Tool Execution Animations */}
                  {msg.toolsExecuting && msg.toolsExecuting.length > 0 && (
                    <div className="mt-2 text-[10px] text-slate-500 flex items-center gap-2 font-semibold bg-slate-50 border border-slate-100 p-2 rounded-lg font-sans">
                      <Loader2 className="w-3 h-3 animate-spin text-blue-600" />
                      {TOOL_LABELS[msg.toolsExecuting[msg.toolsExecuting.length - 1]] ?? "Thinking..."}
                    </div>
                  )}

                  {/* UI Cards */}
                  {msg.cards && msg.cards.map((card, i) => renderCard(card, i))}

                  {/* Pending Action Approval */}
                  {msg.pendingAction && (
                    <div className="mt-3 bg-amber-50 border border-amber-200/60 rounded-xl p-3 shadow-sm">
                      <div className="flex items-center gap-2 text-amber-700 font-bold text-[10px] mb-2 font-sans">
                        <AlertTriangle className="w-3.5 h-3.5" /> Action Requires Approval
                      </div>
                      <div className="text-xs text-amber-900 mb-3 font-sans">
                        Execute <span className="font-mono bg-amber-100 px-1 py-0.5 rounded font-semibold text-amber-950">{msg.pendingAction.action}</span>?
                      </div>
                      <div className="flex gap-2">
                        <button 
                          onClick={() => handleApproveAction(msg.id, msg.pendingAction?.id || "")}
                          disabled={isExecutingAction}
                          className="flex-1 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white text-[11px] font-bold py-2 rounded-lg transition-colors cursor-pointer shadow-sm shadow-amber-500/10 font-sans disabled:opacity-50"
                        >
                          Approve
                        </button>
                        <button 
                          onClick={() => handleCancelAction(msg.id)}
                          disabled={isExecutingAction}
                          className="flex-1 bg-white hover:bg-slate-50 text-slate-600 border border-slate-200/80 text-[11px] font-bold py-2 rounded-lg transition-colors cursor-pointer font-sans disabled:opacity-50"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Suggested Queries */}
                {msg.suggestedQueries && msg.suggestedQueries.length > 0 && msg.id === messages[messages.length - 1].id && (
                  <div className="mt-3 flex flex-wrap gap-1.5 justify-start">
                    {msg.suggestedQueries.map((query, i) => (
                      <button
                        key={i}
                        onClick={() => handleSend(query)}
                        className="text-[11px] bg-white border border-slate-200/80 hover:border-blue-600 text-slate-600 hover:text-blue-600 px-3.5 py-1.5 rounded-full transition-all shadow-sm active:scale-95 cursor-pointer font-sans font-medium bg-slate-50/50 hover:bg-slate-50"
                      >
                        {query}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input Area */}
          <div className="p-3 bg-white border-t border-slate-100 shadow-lg shrink-0">
            <div className="relative">
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                placeholder={isPublic ? "Ask about features, pricing, onboarding, or support..." : `Ask PD Orbit... (${ORBIT_SHORTCUT})`}
                className="w-full bg-slate-50 border border-slate-200/80 rounded-xl pl-4 pr-12 py-3 text-xs focus:outline-none focus:ring-2 focus:ring-blue-600/10 focus:border-blue-600 transition-all placeholder:text-slate-400 font-sans"
                disabled={isTyping}
              />
              <button
                onClick={() => handleSend()}
                disabled={!input.trim() || isTyping}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg disabled:opacity-50 hover:from-blue-700 hover:to-indigo-700 transition-colors shadow-sm cursor-pointer"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
            <div className="text-[10px] text-center text-slate-400 mt-2 font-medium font-sans">
              PD Orbit can make mistakes. Verify important data.
            </div>
          </div>
        </div>
      )}
    </>
  );
}
